"use client";
import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Server, Cpu, HardDrive, Activity, Zap, Clock, Box, Database, List } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

// ── Utilidades ────────────────────────────────────────────────────────────────

function fmt(bytes: number) {
  if (bytes >= 1e9) return (bytes / 1e9).toFixed(1) + " GB";
  if (bytes >= 1e6) return (bytes / 1e6).toFixed(0) + " MB";
  return (bytes / 1e3).toFixed(0) + " KB";
}
function fmtTokens(n: number) {
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(0) + "K";
  return String(n);
}
function fmtUptime(d: any) {
  if (!d) return "—";
  if (d.days > 0) return `${d.days}d ${d.hours}h`;
  if (d.hours > 0) return `${d.hours}h ${d.mins}m`;
  return `${d.mins}m`;
}
function pctColor(pct: number) {
  if (pct > 85) return "var(--bad)";
  if (pct > 65) return "var(--warn)";
  return "var(--ok)";
}

// ── Clasificación de actividad ────────────────────────────────────────────────

// Tipos "ruido de sistema" — se ven atenuados
const ROUTINE_TYPES = new Set([
  "daily_review", "routine_executed", "routine_skipped",
  "heartbeat", "cron_tick", "sync",
]);

function isRoutine(item: any): boolean {
  if (ROUTINE_TYPES.has(item.type)) return true;
  // daily_review con 0 acciones también es ruido
  if (item.type === "daily_review") return true;
  return false;
}

// Emoji por tipo de acción real
const ACTION_EMOJI: Record<string, string> = {
  review_reply:    "💬",
  draft_approved:  "✅",
  draft_rejected:  "❌",
  email_sent:      "📧",
  daily_brief:     "📋",
  task_started:    "▶️",
  task_completed:  "✔️",
  review_received: "⭐",
  email_received:  "📥",
};
const ACTION_COLOR: Record<string, string> = {
  review_reply:    "var(--ok)",
  draft_approved:  "var(--ok)",
  draft_rejected:  "var(--bad)",
  email_sent:      "var(--acc)",
  daily_brief:     "var(--acc)",
  task_started:    "var(--run)",
  task_completed:  "var(--ok)",
  review_received: "var(--warn)",
};

// ── Componentes ───────────────────────────────────────────────────────────────

function GaugeRing({ pct, label, value, sub, icon }: {
  pct: number; label: string; value: string; sub: string; icon: React.ReactNode;
}) {
  const size = 88;
  const r = 32;
  const circ = 2 * Math.PI * r;
  const safePct = Math.min(Math.max(pct, 0), 100);
  const fill = (safePct / 100) * circ;
  const color = pctColor(safePct);

  return (
    <div style={{
      background: "var(--panel)", border: "1px solid var(--line)",
      borderRadius: "12px", padding: "18px 16px",
      display: "flex", alignItems: "center", gap: "16px",
    }}>
      {/* Dona */}
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
          {/* Anillo de fondo siempre completo */}
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="7" />
          {/* Arco de valor */}
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="7"
            strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.8s ease" }} />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: "17px", fontWeight: 800, color, lineHeight: 1 }}>{safePct}%</span>
        </div>
      </div>
      {/* Texto */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "3px" }}>
          <span style={{ color: "var(--tx3)" }}>{icon}</span>
          <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--tx3)" }}>{label}</span>
        </div>
        <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--tx)", lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: "11px", color: "var(--tx3)", marginTop: "3px" }}>{sub}</div>
      </div>
    </div>
  );
}

function StatPill({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{
      background: accent ? "var(--acc-dim)" : "var(--panel)",
      border: `1px solid ${accent ? "var(--acc-dim)" : "var(--line)"}`,
      borderRadius: "10px", padding: "14px 18px",
    }}>
      <div style={{ fontSize: "11px", color: "var(--tx3)", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: "18px", fontWeight: 700, color: accent ? "var(--acc)" : "var(--tx)" }}>{value}</div>
    </div>
  );
}

const STATUS_DOT: Record<string, string> = { online: "var(--ok)", stopped: "var(--bad)", errored: "var(--warn)" };

// ── Panel de actividad ────────────────────────────────────────────────────────

function ActivityLog() {
  const [items, setItems]     = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState<"all" | "actions">("all");

  useEffect(() => {
    fetch(`${API}/activity?limit=200`)
      .then(r => r.json())
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const visible = filter === "actions"
    ? items.filter(i => !isRoutine(i))
    : items;

  return (
    <div style={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "12px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Cabecera + filtro */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--line2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Activity size={13} style={{ color: "var(--tx3)" }} />
          <h2 style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--tx2)", margin: 0 }}>
            Registro de actividad
          </h2>
        </div>
        <div style={{ display: "flex", background: "var(--panel2)", borderRadius: "8px", padding: "2px", gap: "2px" }}>
          {(["all", "actions"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 500, cursor: "pointer", border: "none",
              background: filter === f ? "var(--line)" : "transparent",
              color: filter === f ? "var(--tx)" : "var(--tx3)",
              transition: "all 120ms",
            }}>
              {f === "all" ? "Todo" : "Solo acciones"}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div style={{ overflowY: "auto", maxHeight: "480px" }}>
        {loading ? (
          <div style={{ padding: "32px", textAlign: "center", color: "var(--tx3)" }}><RefreshCw size={14} className="animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center", color: "var(--tx3)", fontSize: "13px" }}>Sin registros</div>
        ) : (
          visible.map(item => {
            const routine = isRoutine(item);
            const emoji = routine ? null : ACTION_EMOJI[item.type];
            const accentColor = routine ? null : (ACTION_COLOR[item.type] ?? "var(--acc)");
            const hora = item.created_at
              ? new Date(item.created_at).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
              : "";

            return (
              <div key={item.id} style={{
                display: "flex", gap: "10px", padding: "7px 16px",
                borderBottom: "1px solid var(--line2)",
                alignItems: "flex-start",
                opacity: routine ? 0.55 : 1,
              }}>
                {/* Indicador lateral */}
                <div style={{
                  width: "3px", borderRadius: "2px", alignSelf: "stretch", flexShrink: 0,
                  background: routine ? "var(--line)" : (accentColor ?? "var(--acc)"),
                  minHeight: "14px",
                }} />
                {/* Contenido */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
                    {emoji && <span style={{ fontSize: "11px" }}>{emoji}</span>}
                    <span style={{
                      fontSize: routine ? "11px" : "12px",
                      color: routine ? "var(--tx3)" : "var(--tx)",
                      fontWeight: routine ? 400 : 600,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "360px",
                    }}>
                      {item.agent_emoji ?? ""} {item.agent_name ?? item.agent_id ?? "—"} · {item.message}
                    </span>
                  </div>
                </div>
                <span style={{ fontSize: "10px", color: "var(--tx3)", flexShrink: 0, whiteSpace: "nowrap", marginTop: "1px" }}>{hora}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Panel coste IA ────────────────────────────────────────────────────────────

function AITokens({ tokens }: { tokens: any }) {
  if (!tokens) return null;
  const agents: any[] = tokens.agents || [];
  const totalCost: number = tokens.totalCost || 0;

  // Escala: ancho mínimo 8% para que los agentes con poco gasto sean visibles
  const maxCost = Math.max(...agents.map((a: any) => a.cost), 0.01);

  return (
    <div style={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "12px", overflow: "hidden" }}>
      {/* Cabecera con total */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--line2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Zap size={13} style={{ color: "var(--tx3)" }} />
          <h2 style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--tx2)", margin: 0 }}>
            Consumo IA por agente
          </h2>
        </div>
        <span style={{ fontSize: "16px", fontWeight: 800, color: "var(--acc)" }}>${totalCost.toFixed(2)}</span>
      </div>

      {/* Filas por agente */}
      {agents.map((a: any, i: number) => {
        const pct = maxCost > 0 ? (a.cost / maxCost) * 100 : 0;
        // Ancho mínimo 8% para que los agentes de bajo gasto sean visibles
        const barW = Math.max(pct, a.cost > 0 ? 8 : 1);
        const sharePct = totalCost > 0 ? ((a.cost / totalCost) * 100).toFixed(0) : "0";

        return (
          <div key={a.id} style={{
            padding: "10px 16px",
            borderBottom: i < agents.length - 1 ? "1px solid var(--line2)" : "none",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ fontSize: "12px", fontWeight: 500, color: "var(--tx)" }}>
                {a.emoji} {a.id}
              </span>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <span style={{ fontSize: "10px", color: "var(--tx3)" }}>{fmtTokens(a.totalTokens)} tok</span>
                <span style={{ fontSize: "11px", color: "var(--tx2)", minWidth: "28px", textAlign: "right" }}>{sharePct}%</span>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--acc)", minWidth: "40px", textAlign: "right" }}>${a.cost.toFixed(2)}</span>
              </div>
            </div>
            {/* Barra con mínimo visible */}
            <div style={{ height: "3px", background: "rgba(255,255,255,.06)", borderRadius: "2px", overflow: "hidden" }}>
              <div style={{
                width: `${barW}%`, height: "100%",
                background: pct > 60 ? "var(--acc)" : "rgba(77,159,255,.5)",
                borderRadius: "2px", transition: "width 0.6s ease",
              }} />
            </div>
          </div>
        );
      })}

      {/* Footer total tokens */}
      <div style={{ padding: "10px 16px", background: "var(--panel2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--tx2)" }}>Total</span>
        <span style={{ fontSize: "11px", color: "var(--tx3)" }}>{fmtTokens(tokens.totalTokens || 0)} tokens</span>
      </div>
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

export default function SystemPage() {
  const [stats, setStats]     = useState<any>(null);
  const [tokens, setTokens]   = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        fetch(`${API}/system/stats`).then(r => r.json()),
        fetch(`${API}/system/tokens`).then(r => r.json()),
      ]);
      setStats(s); setTokens(t); setLastUpdate(new Date());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const s = stats;
  const pm2: any[] = s?.pm2 || [];
  const hasPm2 = pm2.length > 0;

  return (
    <div className="animate-fade-in">

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "4px", display: "flex", alignItems: "center", gap: "9px", color: "var(--tx)" }}>
            <Server size={18} style={{ color: "var(--tx3)" }} /> Sistema
          </h1>
          <p style={{ fontSize: "12px", color: "var(--tx3)" }}>
            {s?.hostname} · {s?.platform}/{s?.arch} · Node {s?.nodeVersion}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {lastUpdate && (
            <span style={{ fontSize: "11px", color: "var(--tx3)" }}>
              {lastUpdate.toLocaleTimeString("es-ES")}
            </span>
          )}
          <button onClick={load} style={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: "8px 14px", background: "var(--panel)", border: "1px solid var(--line)",
            borderRadius: "8px", fontSize: "12px", color: "var(--tx2)", cursor: "pointer",
          }}>
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "80px", color: "var(--tx3)" }}>
          <RefreshCw size={16} className="animate-spin" />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* ── Gauges CPU + RAM + Disco — fila horizontal compacta ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
            <GaugeRing
              pct={s?.cpu?.pct || 0}
              label="CPU" icon={<Cpu size={12} />}
              value={`${s?.cpu?.pct || 0}%`}
              sub={`${s?.cpu?.cores} cores · load ${s?.cpu?.load1?.toFixed(2)}`}
            />
            <GaugeRing
              pct={s?.ram?.pct || 0}
              label="RAM" icon={<Database size={12} />}
              value={fmt(s?.ram?.used || 0)}
              sub={`de ${fmt(s?.ram?.total || 0)}`}
            />
            <GaugeRing
              pct={s?.disk?.pct || 0}
              label="Disco (/)" icon={<HardDrive size={12} />}
              value={fmt(s?.disk?.used || 0)}
              sub={`${fmt(s?.disk?.free || 0)} libre`}
            />
          </div>

          {/* ── Stats rápidas ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
            <StatPill label="Uptime" value={fmtUptime(s?.uptime)} />
            <StatPill label="Load 1m" value={s?.cpu?.load1?.toFixed(2) || "—"} />
            <StatPill label="Load 5m" value={s?.cpu?.load5?.toFixed(2) || "—"} />
            <StatPill label="Coste IA total" value={`$${(tokens?.totalCost || 0).toFixed(2)}`} accent />
          </div>

          {/* ── Procesos PM2 — solo si hay datos ── */}
          {hasPm2 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                <Box size={13} style={{ color: "var(--tx3)" }} />
                <h2 style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--tx2)", margin: 0 }}>
                  Procesos
                </h2>
              </div>
              <div style={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "12px", overflow: "hidden" }}>
                {/* Cabecera tabla */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 70px 80px 80px 60px", gap: "0", padding: "8px 16px", borderBottom: "1px solid var(--line2)" }}>
                  {["Proceso", "Estado", "CPU", "Memoria", "Uptime", "↺"].map(h => (
                    <span key={h} style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--tx3)" }}>{h}</span>
                  ))}
                </div>
                {pm2.map((p: any, i: number) => (
                  <div key={p.name} style={{
                    display: "grid", gridTemplateColumns: "1fr 80px 70px 80px 80px 60px",
                    gap: "0", padding: "9px 16px",
                    borderBottom: i < pm2.length - 1 ? "1px solid var(--line2)" : "none",
                    alignItems: "center",
                  }}>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--tx)" }}>{p.name}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: STATUS_DOT[p.status] || "var(--tx3)", boxShadow: `0 0 5px ${STATUS_DOT[p.status] || "var(--tx3)"}` }} />
                      <span style={{ fontSize: "11px", color: "var(--tx2)" }}>{p.status}</span>
                    </div>
                    <span style={{ fontSize: "12px", color: "var(--tx2)" }}>{p.cpu}%</span>
                    <span style={{ fontSize: "12px", color: "var(--tx2)" }}>{fmt(p.mem || 0)}</span>
                    <span style={{ fontSize: "11px", color: "var(--tx3)" }}>{fmtUptime(p.uptime)}</span>
                    <span style={{ fontSize: "11px", color: "var(--tx3)" }}>{p.restarts}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── 2 columnas: Actividad + Coste IA ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <ActivityLog />
            <AITokens tokens={tokens} />
          </div>

          {/* ── Unidades de almacenamiento ── */}
          {(s?.disk?.disks || []).length > 1 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                <HardDrive size={13} style={{ color: "var(--tx3)" }} />
                <h2 style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--tx2)", margin: 0 }}>
                  Unidades de almacenamiento
                </h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "10px" }}>
                {(s.disk.disks || []).map((d: any) => {
                  const color = d.pct > 85 ? "var(--bad)" : d.pct > 65 ? "var(--warn)" : "var(--ok)";
                  const nameMap: Record<string, string> = {
                    "/": "Sistema",
                    "/media/varadero/BCE4287EE4283CD0": "Disco 1",
                    "/media/varadero/DADES": "Dades",
                  };
                  const name = nameMap[d.mount] || d.mount.split("/").pop() || d.device;
                  return (
                    <div key={d.device} style={{
                      background: "var(--panel)", border: `1px solid ${d.pct > 85 ? "rgba(239,68,68,.3)" : "var(--line)"}`,
                      borderRadius: "10px", padding: "14px 16px",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "2px", color: "var(--tx)" }}>{name}</div>
                          <div style={{ fontSize: "11px", color: "var(--tx3)" }}>{d.device} · {d.mount}</div>
                        </div>
                        <span style={{ fontSize: "15px", fontWeight: 800, color }}>{d.pct}%</span>
                      </div>
                      <div style={{ height: "4px", background: "rgba(255,255,255,.06)", borderRadius: "3px", overflow: "hidden", marginBottom: "8px" }}>
                        <div style={{ width: `${d.pct}%`, height: "100%", background: color, borderRadius: "3px", transition: "width 0.6s ease" }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--tx3)" }}>
                        <span>Usado: <b style={{ color: "var(--tx2)" }}>{fmt(d.used)}</b></span>
                        <span>Libre: <b style={{ color: "var(--tx2)" }}>{fmt(d.free)}</b></span>
                        <span>Total: <b style={{ color: "var(--tx2)" }}>{fmt(d.total)}</b></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
